import { useEffect, useState } from 'react';
import { fetchUserBranches } from '../../auth/application/auth';
import { awaitSessionBootstrap, type BootstrapMobileSessionPayload } from '../session/hydrateSessionCachesFromBootstrap';
import { getLastSelectedBranchId } from '../../shared/localPrefs';
import { isDatabaseUnavailableError } from '../../shared/localDb';
import { devWarn } from '../../shared/dev/devLog';
import { useSessionStore, type BranchAccess } from '../session/store/useSessionStore';
import type { AppUpdateConfig } from '../session/bootstrapMobileSessionService';

export function useBranchBootstrap({
  userId,
  accountSetupResolved,
  requiresOwnerOnboarding,
  hasOrgAndBranches,
  pendingEmployeeInvitation,
}: {
  userId: string | undefined;
  accountSetupResolved: boolean;
  requiresOwnerOnboarding: boolean | null;
  hasOrgAndBranches: boolean;
  pendingEmployeeInvitation: unknown;
}): {
  initialBranchSelectionReady: boolean;
  employeeAccessReady: boolean;
  appUpdateConfig: AppUpdateConfig | null;
} {
  const [initialBranchSelectionReady, setInitialBranchSelectionReady] = useState(false);
  const [employeeAccessReady, setEmployeeAccessReady] = useState(false);
  const [appUpdateConfig, setAppUpdateConfig] = useState<AppUpdateConfig | null>(null);

  useEffect(() => {
    if (!userId) {
      setInitialBranchSelectionReady(true);
      setEmployeeAccessReady(true);
      return;
    }

    if (!accountSetupResolved) {
      setInitialBranchSelectionReady(false);
      setEmployeeAccessReady(false);
      return;
    }

    if (requiresOwnerOnboarding === true && !hasOrgAndBranches) {
      useSessionStore.getState().setBranchesLoading(false);
      setInitialBranchSelectionReady(true);
      setEmployeeAccessReady(true);
      return;
    }

    // Invitación pendiente sin org propia: no hay sucursales que cargar.
    if (pendingEmployeeInvitation && !hasOrgAndBranches) {
      useSessionStore.getState().setBranchesLoading(false);
      setInitialBranchSelectionReady(true);
      setEmployeeAccessReady(true);
      return;
    }

    const { branches: currentBranches, currentBranchId, setBranches, setCurrentBranch, setBranchesLoading, loadBranchesFromCache } =
      useSessionStore.getState();
    if (currentBranches.length > 0) {
      if (!currentBranchId && currentBranches.length === 1) {
        setCurrentBranch(currentBranches[0].id);
      }
      setBranchesLoading(false);
      setInitialBranchSelectionReady(true);
      setEmployeeAccessReady(true);
      return;
    }

    let cancelled = false;
    setInitialBranchSelectionReady(false);
    setEmployeeAccessReady(false);
    setBranchesLoading(true);

    const applyLastBranchSelection = async (list: BranchAccess[]) => {
      if (list.length === 0) return;
      const last = await getLastSelectedBranchId();
      const { setCurrentBranch } = useSessionStore.getState();
      // Solo considerar sucursales activas para restaurar/seleccionar (#628).
      // Una branch inactiva guardada en localStorage no debe usarse como
      // current: si no, orders/sales/etc. muestran data vacía.
      const activeList = list.filter((branch) => branch.isActive !== false);
      if (last && activeList.some((branch) => branch.id === last)) {
        setCurrentBranch(last);
      } else if (last) {
        // El último seleccionado ya no está activo: caer al primer activo
        // (o null si no hay ninguna activa).
        setCurrentBranch(activeList.length > 0 ? activeList[0].id : undefined);
      } else if (activeList.length === 1) {
        setCurrentBranch(activeList[0].id);
      }
    };

    void (async () => {
      try {
        const payload: BootstrapMobileSessionPayload | null = await awaitSessionBootstrap(userId);
        if (cancelled) return;

        setAppUpdateConfig(payload?.app_update ?? null);

        let list = payload?.branches ?? [];
        if (list.length === 0) {
          try {
            list = await fetchUserBranches();
          } catch {
            await loadBranchesFromCache();
            list = useSessionStore.getState().branches;
          }
        }

        if (cancelled) return;
        if (list.length > 0) {
          setBranches(list);
          await applyLastBranchSelection(list);
        }

        // A4: Empleado revocado — ya no hacemos logout. AppNavigator muestra NoBusinessAccessScreen.
        // El usuario puede seguir comprando en el portal o eliminar su cuenta desde ahí.
      } catch (e) {
        if (!isDatabaseUnavailableError(e)) {
          devWarn('[App] Bootstrap sesión / sucursales', e);
        }
        if (!cancelled) {
          try {
            await loadBranchesFromCache();
            const { branches: cachedBranches } = useSessionStore.getState();
            if (cachedBranches.length > 0) {
              await applyLastBranchSelection(cachedBranches);
            }
          } catch (cacheErr) {
            if (!isDatabaseUnavailableError(cacheErr)) {
              devWarn('[App] Caché de sucursales offline', cacheErr);
            }
          }
        }
      } finally {
        if (!cancelled) {
          setBranchesLoading(false);
          setInitialBranchSelectionReady(true);
          setEmployeeAccessReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, accountSetupResolved, requiresOwnerOnboarding, hasOrgAndBranches, pendingEmployeeInvitation]);

  return { initialBranchSelectionReady, employeeAccessReady, appUpdateConfig };
}
